import { useMemo } from "react";
import { useWizard } from "../wizard-state";
import { AlertsPanel } from "../AlertsPanel";
import { evaluateAdminStatus, evaluateClinicalStatus, validateClose } from "../rules";
import type { Decision, YesNo } from "@/types/wizard";

const DECISIONS: Decision[] = [
  "Apto",
  "Reemplazar con paciente de lista de espera",
  "Reprogramar por causa administrativa",
  "Reprogramar por causa clínica",
];

export function Step4Resultado({ closeError }: { closeError: string | null }) {
  const { data, set } = useWizard();

  const admin    = useMemo(() => evaluateAdminStatus(data),    [data]);
  const clinical = useMemo(() => evaluateClinicalStatus(data), [data]);
  const closeMsg = useMemo(() => validateClose(data, admin, clinical), [data, admin, clinical]);

  const requiereGestionAuto = admin.estado === "pendiente_subsanable" || clinical.semaforo === "amarillo";
  const ambasCausas = admin.estado === "pendiente_no_subsanable" && clinical.semaforo === "rojo";

  return (
    <div className="wizard-section-group">
      <div className="wizard-section">
        <h4 className="wizard-section-title">Resultado</h4>
        <div className="result-cards">
          <div className={`result-highlight result-highlight--${admin.estado.replace("_", "-")}`}>
            <div className="result-highlight-label">{admin.texto}</div>
            <div className="result-highlight-detail">{admin.detalle}</div>
          </div>
          <div className={`semaforo-grande semaforo-${clinical.semaforo}`}>
            <div className="semaforo-dot"></div>
            <div>
              <div className="semaforo-label">
                {clinical.semaforo === "verde" && "🟢 APTO CLÍNICAMENTE"}
                {clinical.semaforo === "amarillo" && "🟡 REQUIERE GESTIÓN CON ANESTESIA"}
                {clinical.semaforo === "rojo" && "🔴 REPROGRAMAR POR CAUSA CLÍNICA"}
              </div>
              <div className="semaforo-sub">{clinical.detalle}</div>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <h5 className="text-muted mb-8">Alertas detectadas</h5>
          <AlertsPanel admin={admin.alertas} clinical={clinical.alertas} />
        </div>
      </div>

      {(requiereGestionAuto || ambasCausas) && (
        <div className="wizard-section wizard-section--warn">
          <h4 className="wizard-section-title">Gestión del evaluador</h4>
          <p className="text-muted mb-8">
            {ambasCausas
              ? "Existen alertas clínicas y administrativas. La decisión final debe ser Reprogramar por causa clínica."
              : "Este caso requiere gestión antes del cierre."}
          </p>

          <div className="wizard-grid">
            <label className="form-field">
              <span>¿Requiere gestión?</span>
              <select value={data.requiereGestion} onChange={(e) => set("requiereGestion", e.target.value as YesNo)}>
                <option value="si">Sí</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="form-field">
              <span>Tipo de gestión</span>
              <select value={data.tipoGestion} onChange={(e) => set("tipoGestion", e.target.value as typeof data.tipoGestion)}>
                <option value="">— seleccionar —</option>
                <option value="subsanacion_administrativa">Subsanación administrativa</option>
                <option value="consulta_anestesia">Consulta con anestesia</option>
                <option value="ambas">Ambas</option>
              </select>
            </label>
            <label className="form-field form-field--full">
              <span>Acciones realizadas por el evaluador</span>
              <textarea rows={2} value={data.accionesEvaluador} onChange={(e) => set("accionesEvaluador", e.target.value)} />
            </label>
            {(data.tipoGestion === "consulta_anestesia" || data.tipoGestion === "ambas") && (
              <>
                <label className="form-field">
                  <span>Profesional / área consultada</span>
                  <input value={data.profesionalConsultado} onChange={(e) => set("profesionalConsultado", e.target.value)} />
                </label>
                <label className="form-field">
                  <span>Respuesta / indicación recibida</span>
                  <input value={data.respuestaRecibida} onChange={(e) => set("respuestaRecibida", e.target.value)} />
                </label>
              </>
            )}
            <label className="form-field form-field--full">
              <span>Evidencia de cierre</span>
              <textarea rows={2} value={data.evidenciaCierre} onChange={(e) => set("evidenciaCierre", e.target.value)} />
            </label>
          </div>
        </div>
      )}

      <div className="wizard-section">
        <h4 className="wizard-section-title">Decisión final</h4>
        <div className="decision-buttons">
          {DECISIONS.map((d) => (
            <button
              key={d}
              type="button"
              className={`decision-btn ${data.decisionFinalEvaluador === d ? "active" : ""}`}
              onClick={() => set("decisionFinalEvaluador", d)}
            >
              {d}
            </button>
          ))}
        </div>

        {(closeError || (data.decisionFinalEvaluador && closeMsg)) && (
          <div className="alert-inline alert-inline--danger alert-inline--md mt-8">
            ⚠️ {closeError || closeMsg}
          </div>
        )}
      </div>
    </div>
  );
}
